import React, { useEffect, useState } from 'react';
import { Button, Popconfirm, Space, Table, Tag, message } from 'antd';
import { DeleteOutlined, EditOutlined, LinkOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ListPageLayout from '../../components/ListPage/ListPageLayout';
import api from '../../services/api';
import { useUniverse } from '../../contexts/UniverseContext';

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3003';

const Elseworlds = () => {
  const navigate = useNavigate();
  const [elseworlds, setElseworlds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const { selectedUniverseId } = useUniverse();

  const fetchElseworlds = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedUniverseId) {
        params.append('context_universe_id', selectedUniverseId);
      }
      const response = await api.get(`/api/admin/elseworlds?${params.toString()}`);
      setElseworlds(response.data?.elseworlds || []);
    } catch (error) {
      message.error(error.response?.data?.error || 'Erreur lors du chargement des elseworlds');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchElseworlds();
  }, [selectedUniverseId]);

  const handleDelete = async (elseworldId) => {
    try {
      await api.delete(`/api/admin/elseworlds/${elseworldId}`);
      message.success('Elseworld supprimé avec succès');
      fetchElseworlds();
    } catch (error) {
      message.error(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const filteredElseworlds = elseworlds.filter((elseworld) => {
    if (!searchValue.trim()) {
      return true;
    }
    const query = searchValue.toLowerCase();
    return (
      elseworld.name?.toLowerCase().includes(query)
      || elseworld.slug?.toLowerCase().includes(query)
      || elseworld.parentUniverse?.name?.toLowerCase().includes(query)
    );
  });

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Nom', dataIndex: 'name', key: 'name' },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    {
      title: 'Univers parent',
      dataIndex: ['parentUniverse', 'name'],
      key: 'parentUniverse',
      render: (value) => value || '-',
    },
    {
      title: 'Forums',
      dataIndex: 'forumsCount',
      key: 'forumsCount',
      width: 90,
      render: (count) => <Tag>{count ?? 0}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => {
        const publicUrl = record.parentUniverse?.slug
          ? `${FRONTEND_URL}/univers/${record.parentUniverse.slug}/elseworld/${record.slug}`
          : null;

        return (
          <Space>
            {publicUrl && (
              <Button
                type="text"
                icon={<LinkOutlined />}
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Voir sur le site"
              />
            )}
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(`/elseworlds/edit/${record.id}`)}
              title="Modifier"
            />
            <Popconfirm
              title="Supprimer cet elseworld ?"
              description="Seuls les elseworlds sans forums peuvent être supprimés."
              okText="Oui"
              cancelText="Non"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button type="text" danger icon={<DeleteOutlined />} title="Supprimer" />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <ListPageLayout
      breadcrumbs={[{ label: 'Elseworlds' }]}
      actions={[{ label: 'Créer un elseworld', onClick: () => navigate('/elseworlds/new') }]}
      searchPlaceholder="Rechercher un elseworld..."
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      onSearch={setSearchValue}
      filters={[]}
      onResetFilters={() => setSearchValue('')}
    >
      <Table
        rowKey="id"
        dataSource={filteredElseworlds}
        columns={columns}
        loading={loading}
        pagination={{
          pageSize: 20,
          showTotal: (total) => `Total: ${total} elseworlds`,
        }}
      />
    </ListPageLayout>
  );
};

export default Elseworlds;
